import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { getShopAdmins, createShopAdmin, updateShopAdmin, deleteShopAdmin, getRestaurants } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Store, UserCog } from 'lucide-react';

const emptyForm = { email: '', password: '', name: '', restaurant_ids: [] };

export default function ShopAdmins() {
    const [admins, setAdmins] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const fetchData = useCallback(async () => {
        try {
            const [adminsRes, restRes] = await Promise.all([getShopAdmins(), getRestaurants()]);
            setAdmins(adminsRes.data);
            setRestaurants(restRes.data);
        } catch (err) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setShowDialog(true);
    };

    const openEdit = (admin) => {
        setEditing(admin);
        setForm({ email: admin.email, password: '', name: admin.name, restaurant_ids: admin.restaurant_ids || [] });
        setShowDialog(true);
    };

    const toggleRestaurant = (id) => {
        setForm(prev => ({
            ...prev,
            restaurant_ids: prev.restaurant_ids.includes(id)
                ? prev.restaurant_ids.filter(r => r !== id)
                : [...prev.restaurant_ids, id]
        }));
    };

    const handleSave = async () => {
        if (!form.email || !form.name) {
            toast.error('Email and name are required');
            return;
        }
        if (!editing && !form.password) {
            toast.error('Password is required for new accounts');
            return;
        }
        try {
            if (editing) {
                const payload = { name: form.name, email: form.email, restaurant_ids: form.restaurant_ids };
                if (form.password) payload.password = form.password;
                await updateShopAdmin(editing.id, payload);
                toast.success('Shop admin updated');
            } else {
                await createShopAdmin(form);
                toast.success('Shop admin created');
            }
            setShowDialog(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this shop admin?')) return;
        try {
            await deleteShopAdmin(id);
            toast.success('Shop admin deleted');
            fetchData();
        } catch (err) {
            toast.error('Failed to delete');
        }
    };

    const getRestaurantName = (id) => restaurants.find(r => r.id === id)?.name || id;

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="w-10 h-10 border-4 border-[#C75D4E] border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-[#2B2A27]">Shop Admins</h1>
                        <p className="text-sm text-[#69665B]">Manage admin accounts for each restaurant</p>
                    </div>
                    <Button onClick={openCreate} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white gap-2" data-testid="create-shop-admin-btn">
                        <Plus className="w-4 h-4" /> Add Shop Admin
                    </Button>
                </div>

                {admins.length === 0 ? (
                    <Card className="p-12 text-center border-[#E5E1D1]">
                        <UserCog className="w-12 h-12 mx-auto text-[#969282] mb-4" />
                        <h3 className="text-lg font-medium text-[#2B2A27] mb-2">No Shop Admins Yet</h3>
                        <p className="text-sm text-[#69665B] mb-4">Create admin accounts and assign them to restaurants</p>
                        <Button onClick={openCreate} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white gap-2">
                            <Plus className="w-4 h-4" /> Create First Admin
                        </Button>
                    </Card>
                ) : (
                    <div className="grid gap-4">
                        {admins.map(admin => (
                            <Card key={admin.id} className="p-5 border-[#E5E1D1]" data-testid={`shop-admin-${admin.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-11 h-11 bg-[#C75D4E]/10 rounded-xl flex items-center justify-center">
                                            <UserCog className="w-5 h-5 text-[#C75D4E]" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-[#2B2A27]">{admin.name}</p>
                                            <p className="text-sm text-[#69665B]">{admin.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => openEdit(admin)} className="gap-1" data-testid={`edit-admin-${admin.id}`}>
                                            <Pencil className="w-3.5 h-3.5" /> Edit
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => handleDelete(admin.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1" data-testid={`delete-admin-${admin.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                                {admin.restaurant_ids?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {admin.restaurant_ids.map(rid => (
                                            <span key={rid} className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F5F3E9] text-[#69665B] text-xs rounded-full border border-[#E5E1D1]">
                                                <Store className="w-3 h-3" /> {getRestaurantName(rid)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Edit Shop Admin' : 'New Shop Admin'}</DialogTitle>
                        <DialogDescription>{editing ? 'Update admin details and restaurant access' : 'Create an account for a restaurant owner'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Shop owner name" className="border-[#E5E1D1] rounded-xl" data-testid="admin-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="shop@example.com" className="border-[#E5E1D1] rounded-xl" data-testid="admin-email-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>{editing ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
                            <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Password" className="border-[#E5E1D1] rounded-xl" data-testid="admin-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Assign Restaurants</Label>
                            <div className="max-h-48 overflow-y-auto space-y-2 border border-[#E5E1D1] rounded-xl p-3">
                                {restaurants.length === 0 ? (
                                    <p className="text-sm text-[#969282]">No restaurants created yet</p>
                                ) : restaurants.map(r => (
                                    <label key={r.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${form.restaurant_ids.includes(r.id) ? 'bg-[#C75D4E]/5' : 'hover:bg-[#F5F3E9]'}`}>
                                        <input type="checkbox" checked={form.restaurant_ids.includes(r.id)} onChange={() => toggleRestaurant(r.id)} className="accent-[#C75D4E]" />
                                        <span className="text-sm text-[#2B2A27]">{r.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                            <Button onClick={handleSave} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white" data-testid="save-admin-btn">
                                {editing ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
